import { useTranslation } from "react-i18next";
import type { Provider } from "../../../../../../store/reseller-admin/providersStore";
import type { ThemeClasses } from "../types";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface ProviderDetailsModalProps {
  provider: Provider;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: (provider: Provider) => void;
  onDelete: (provider: Provider) => void;
}

export default function ProviderDetailsModal({
  provider,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
  onDelete,
}: ProviderDetailsModalProps) {
  const { t } = useTranslation("resellerAdmin");
  const { isUserDev } = useIsUserDev();

  const adapterLabel = provider.adapter
    ? t(`providers.options.adapter.${provider.adapter}`, { defaultValue: provider.adapter })
    : t("providers.noData");
  const authLabel = provider.auth_strategy
    ? t(`providers.options.authStrategy.${provider.auth_strategy}`, {
        defaultValue: provider.auth_strategy,
      })
    : t("providers.noData");

  const endpointEntries = Object.entries(provider.endpoints || {});

  const Row = ({ label, value }: { label: string; value: string }) => (
    <div>
      <p className={`text-xs ${themeClasses.textSecondary}`}>{label}</p>
      <p className={`${themeClasses.text} break-words`}>{value}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl ${themeClasses.bg} shadow-2xl`}>
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}>
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>
            <i className="ri-cloud-line mr-2"></i>
            {t("providers.modal.detailsTitle")}
          </h2>
          <button aria-label={t("general.close", { ns: "common" })} onClick={onClose} className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}>
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-primary-500/15" : "bg-primary-50"}`}>
              <i className="ri-cloud-line text-xl text-primary-500"></i>
            </div>
            <div>
              <h3 className={`text-lg font-bold ${themeClasses.text}`}>{provider.name}</h3>
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                  provider.active
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                }`}
              >
                {provider.active ? t("providers.active") : t("providers.inactive")}
              </span>
            </div>
          </div>

          <div className={`p-4 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
            <h4 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3`}>
              {t("providers.sections.connection")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Row label={t("providers.fields.adapter")} value={adapterLabel} />
              <Row label={t("providers.fields.authStrategy")} value={authLabel} />
              <Row
                label={t("providers.fields.discoveryInterval")}
                value={provider.default_discovery_interval_sec || t("providers.noData")}
              />
              <Row
                label={t("providers.fields.pollInterval")}
                value={provider.default_poll_interval_sec || t("providers.noData")}
              />
              <Row
                label={t("providers.fields.supportsDiscovery")}
                value={provider.supports_discovery ? t("providers.yes") : t("providers.no")}
              />
            </div>
          </div>

          <div>
            <h4 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3`}>
              {t("providers.sections.endpoints")}
            </h4>
            {endpointEntries.length > 0 ? (
              <div className="space-y-2">
                {endpointEntries.map(([key, value]) => (
                  <div
                    key={key}
                    className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}
                  >
                    <span className={`text-sm font-medium ${themeClasses.text} shrink-0`}>{key}</span>
                    <span className={`text-xs ${themeClasses.textSecondary} truncate`}>{value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${themeClasses.textSecondary}`}>{t("providers.noData")}</p>
            )}
          </div>

          <div>
            <h4 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3`}>
              {t("providers.sections.credentials")}
            </h4>
            {provider.credentials_schema && provider.credentials_schema.length > 0 ? (
              <div className="space-y-2">
                {provider.credentials_schema.map((cred, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}
                  >
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                        {cred.label || cred.field}
                      </p>
                      <p className={`text-xs ${themeClasses.textSecondary} truncate`}>{cred.field}</p>
                    </div>
                    {cred.secret && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <i className="ri-lock-line"></i>
                        {t("providers.fields.credentialSecret")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${themeClasses.textSecondary}`}>{t("providers.noCredentials")}</p>
            )}
          </div>
        </div>

        {isUserDev && (
          <div className={`sticky bottom-0 flex items-center justify-end gap-3 p-6 border-t ${themeClasses.border} ${themeClasses.bg}`}>
            <button
              onClick={() => onDelete(provider)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
            >
              <i className="ri-delete-bin-line mr-1"></i>
              {t("providers.actions.delete")}
            </button>
            <button
              onClick={() => onEdit(provider)}
              className="px-6 py-2.5 rounded-xl text-sm font-medium bg-primary-500 text-gray-900 hover:bg-primary-600 transition-all shadow"
            >
              <i className="ri-edit-line mr-1"></i>
              {t("providers.actions.edit")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
